import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { PhoneCall, Loader2, Mic, X, Volume2, Play, Send, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { RecordingPlayer } from '@/components/calls/CallLogPanel';

const QUICK_INTENTS = [
  'Book a free hearing test appointment at the nearest clinic.',
  'Confirm their upcoming appointment and remind about free hearing test.',
  'Follow up on their 7-day free trial — ask how the device feels.',
  'Check in on a lead that went cold — gently offer a free home-visit test.',
  'Confirm invoice payment and schedule the home fitting.',
  'Collect feedback after device fitting; ask if any adjustment is needed.',
];

export default function CallPatientDialog({
  open, onOpenChange, ticketId, patientName, patientMobile, onInitiated,
}) {
  const [intent, setIntent] = useState(QUICK_INTENTS[0]);
  const [phone, setPhone] = useState(patientMobile || '');
  const [initiating, setInitiating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);  // {greeting, audio_url}
  const [sendingLink, setSendingLink] = useState(false);
  const [sentLink, setSentLink] = useState(null); // {link, to, whatsapp}
  const [copied, setCopied] = useState(false);
  const [callId, setCallId] = useState(null);
  const [callDoc, setCallDoc] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { setPhone(patientMobile || ''); }, [patientMobile, open]);
  useEffect(() => {
    if (!open) {
      setCallId(null); setCallDoc(null); setPreview(null); setSentLink(null); setCopied(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  }, [open]);

  const sendBrowserLink = async () => {
    if (!phone.trim()) return toast.error('Phone number is required');
    setSendingLink(true);
    try {
      const { data } = await api.post('/convai/send-link', {
        ticket_id: ticketId, phone: phone.trim(),
        patient_name: patientName, intent: intent.trim(),
      });
      setSentLink(data);
      if (data?.whatsapp?.success) {
        toast.success(`WhatsApp link sent to ${data.to}`);
      } else {
        toast.message('Link ready — WhatsApp send had an issue, copy manually below');
      }
      onInitiated && onInitiated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create link');
    } finally { setSendingLink(false); }
  };

  const copyLink = async () => {
    if (!sentLink?.link) return;
    try {
      await navigator.clipboard.writeText(sentLink.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post('/calls/preview', {
        ticket_id: ticketId, phone: phone.trim(), intent: intent.trim(),
      });
      setPreview(data);
      try { new Audio(data.audio_url).play(); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const initiate = async () => {
    if (!phone.trim()) return toast.error('Phone number is required');
    setInitiating(true);
    try {
      const { data } = await api.post('/calls/live/outbound', {
        ticket_id: ticketId, phone: phone.trim(), intent: intent.trim(),
      });
      setCallId(data.call_id);
      toast.success(`Dialing ${data.to}…`);
      onInitiated && onInitiated();
      // Poll call status every 3s (ElevenLabs syncs transcript once the call ends)
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/calls/${data.call_id}`);
          setCallDoc(r.data);
          if (['completed', 'failed', 'busy', 'no-answer', 'canceled'].includes(r.data.status)) {
            clearInterval(pollRef.current); pollRef.current = null;
            onInitiated && onInitiated();
          }
        } catch {}
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Call failed to initiate');
    } finally { setInitiating(false); }
  };

  const close = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    onOpenChange(false);
  };

  const statusBadge = (() => {
    const s = callDoc?.status;
    if (!s) return null;
    const map = {
      dialing: ['bg-amber-100 text-amber-800', 'Dialing'],
      initiated: ['bg-amber-100 text-amber-800', 'Initiated'],
      ringing: ['bg-blue-100 text-blue-800', 'Ringing'],
      answered: ['bg-indigo-100 text-indigo-800', 'Answered'],
      in_progress: ['bg-indigo-100 text-indigo-800', 'Talking'],
      'in-progress': ['bg-indigo-100 text-indigo-800', 'Talking'],
      completed: ['bg-emerald-100 text-emerald-800', 'Completed'],
      failed: ['bg-red-100 text-red-800', 'Failed'],
      busy: ['bg-red-100 text-red-800', 'Busy'],
      'no-answer': ['bg-stone-200 text-stone-700', 'No Answer'],
      canceled: ['bg-stone-200 text-stone-700', 'Canceled'],
    };
    const [cls, label] = map[s] || ['bg-stone-100 text-stone-700', s];
    return <Badge className={`${cls} text-[11px]`}>{label}</Badge>;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="call-patient-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-rose-600" /> AI Call — {patientName || 'Patient'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Priya calls in warm Hinglish in real time, checks free calendar slots, books the appointment
            and saves the recording + transcript on this ticket.
          </DialogDescription>
        </DialogHeader>

        {!callId && (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500">Phone</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder="+91 98765 43210" data-testid="call-phone-input" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500">Call Intent</label>
              <Textarea
                value={intent} onChange={(e) => setIntent(e.target.value)}
                rows={3} className="text-sm" data-testid="call-intent-input"
                placeholder="What should the agent accomplish on this call?"
              />
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {QUICK_INTENTS.map((q, i) => (
                  <button key={q} onClick={() => setIntent(q)} data-testid={`call-quick-intent-${i}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${intent === q ? 'bg-stone-900 text-white border-stone-900' : 'border-stone-300 bg-white hover:bg-stone-100 text-stone-700'}`}>
                    {q.slice(0, 40)}…
                  </button>
                ))}
              </div>
            </div>

            {preview && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <Volume2 className="w-3.5 h-3.5 text-indigo-600" />
                  <span className="text-[11px] font-semibold text-indigo-900 uppercase tracking-wider">Priya will say</span>
                  <button
                    onClick={() => { try { new Audio(preview.audio_url).play(); } catch {} }}
                    className="ml-auto text-[10px] text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1">
                    <Play className="w-3 h-3" /> Replay
                  </button>
                </div>
                <div className="text-xs text-stone-800 italic">"{preview.greeting}"</div>
              </div>
            )}

            {sentLink && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <Send className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-[11px] font-semibold text-emerald-900 uppercase tracking-wider">
                    {sentLink.whatsapp?.success ? 'WhatsApp sent' : 'Link ready — share manually'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <code className="flex-1 text-[10px] bg-white rounded px-1.5 py-1 border border-emerald-200 truncate">
                    {sentLink.link}
                  </code>
                  <button
                    onClick={copyLink}
                    className="text-[10px] text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 shrink-0"
                    data-testid="call-copy-link">
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <div className="text-[10px] text-stone-500">
                  Patient will tap the link → open their browser → talk to Priya live. No phone number dial, no Twilio needed.
                </div>
              </div>
            )}
          </div>
        )}

        {callId && (
          <div className="space-y-3">
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 flex items-center gap-3">
              <Mic className={`w-5 h-5 ${callDoc?.status === 'completed' ? 'text-emerald-600' : 'text-rose-600 animate-pulse'}`} />
              <div className="flex-1">
                <div className="text-sm font-medium text-stone-800">Call ID: <span className="font-mono text-[11px]">{callId.slice(-8)}</span></div>
                <div className="text-[11px] text-stone-500">To: {callDoc?.to || phone} · Duration: {callDoc?.duration ? `${callDoc.duration}s` : '—'}</div>
              </div>
              {statusBadge}
            </div>
            {callDoc?.booked_slot && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs text-emerald-900 font-medium" data-testid="call-booked-slot">
                Appointment booked: {new Date(callDoc.booked_slot).toLocaleString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            {callDoc?.summary && <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2"><b>Summary:</b> {callDoc.summary}</div>}
            {callDoc?.status === 'completed' && callDoc?.has_recording && <RecordingPlayer callId={callId} />}
            {callDoc?.turns?.length > 0 && (
              <div className="max-h-60 overflow-y-auto space-y-1.5 bg-white border border-stone-200 rounded-lg p-3">
                {callDoc.turns.map((t, i) => (
                  <div key={i} className={`text-xs ${t.direction === 'patient' ? 'text-stone-800' : 'text-rose-700'}`}>
                    <span className="font-semibold">{t.direction === 'patient' ? 'Patient' : 'Priya'}:</span> {t.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-1.5 flex-wrap">
          {!callId ? (
            <>
              <Button variant="ghost" onClick={close} disabled={initiating || previewing || sendingLink}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                variant="outline" onClick={previewVoice}
                disabled={previewing || initiating || sendingLink || !intent.trim()}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                data-testid="call-preview-btn"
              >
                {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Volume2 className="w-4 h-4 mr-1.5" />}
                Preview
              </Button>
              <Button
                variant="outline" onClick={sendBrowserLink}
                disabled={sendingLink || !phone.trim() || !intent.trim()}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="call-send-link-btn"
                title="Send WhatsApp link — no Twilio needed"
              >
                {sendingLink ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                Send WhatsApp Link
              </Button>
              <Button onClick={initiate} disabled={initiating || !phone.trim()}
                      className="bg-rose-600 hover:bg-rose-700" data-testid="call-start-btn"
                      title="Dials over Twilio — needs KYC">
                {initiating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PhoneCall className="w-4 h-4 mr-1.5" />}
                Dial Phone
              </Button>
            </>
          ) : (
            <Button onClick={close} variant="outline">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
